import Header from "components/Header";
import { useState, useCallback } from "react";
import { Box, Container, SimpleGrid, Stack, Text, useToast } from "@chakra-ui/react";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { createServerSupabaseClient } from "@supabase/auth-helpers-nextjs";
import BlackButton from "@/components/BlackButton";
import StyledLink from "@/components/StyledLink";
import StyledHeading from "@/components/StyledHeading";
import StyledStack from "@/components/StyledStack";
import StyledInput from "@/components/StyledInput";
import ErrorText from "@/components/shared/typography/ErrorText";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/router";
import Layout from "@/components/Layout";
import { PASSWORD_RESET_REDIRECT_URL } from "utils/constants";

const forgotSchema = yup.object().shape({
  email: yup.string().email().trim().required(),
});

const Forgot = () => {
  const [isLoading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const supabaseClient = useSupabaseClient();
  const router = useRouter();
  const toast = useToast();

  const { register, handleSubmit, formState } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: true,
    resolver: yupResolver(forgotSchema),
  });
  const { errors } = formState;

  const onSendLink = useCallback(async ({ email }) => {
    setLoading(true);
    setFormError(null);
    try {
      const { error } = await supabaseClient.auth.resetPasswordForEmail(
        email,
        {
          redirectTo: PASSWORD_RESET_REDIRECT_URL,
        }
      );
      if (error) {
        throw error;
      } else {
        setLoading(false);
        toast({
          title: "Password Reset Request",
          description: "A password reset link has been sent to your email.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        router.push("/login");
      }
    } catch (error) {
      setFormError(error.message);
      setLoading(false);
    }
  }, [router, supabaseClient.auth, toast]);
  return (
    <Layout>
      <Header browserTitle="WellFitly - Forgot password" />
      <Container
        as={SimpleGrid}
        minW={"100vw"}
        bg={"gray.50"}
        minH={"75vh"}
        py={{ base: 10, sm: 5, lg: 24 }}
      >
        <StyledStack>
          <Stack align={"center"}>
            <StyledHeading
              mainTitle="Password Reset"
              subTitle="You will receive a reset link."
            />
            <Text>
              Remember your password?{" "}
              <StyledLink href="/login">Login</StyledLink>
            </Text>
          </Stack>

          <Box as={"form"} mt={10} onSubmit={handleSubmit(onSendLink)}>
            <Stack spacing={4}>
              <StyledInput
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                isInvalid={errors.email}
                register={register}
                errors={errors}
              />
              <Stack spacing={4}>
                <BlackButton
                  type="submit"
                  id="btn-forgot-password"
                  isLoading={isLoading}
                  buttonText="Send reset link"
                />
                <Box>{formError && <ErrorText text={formError} />}</Box>
              </Stack>
            </Stack>
          </Box>
        </StyledStack>
      </Container>
    </Layout>
  );
};

export const getServerSideProps = async ({ req, res }) => {
  const supabaseServerClient = createServerSupabaseClient({ req, res });
  const {
    data: { session },
  } = await supabaseServerClient.auth.getSession();

  if (session) {
    return {
      redirect: {
        permanent: false,
        destination: `/dashboard`,
      },
    };
  }

  return {
    props: {},
  };
};

export default Forgot;
